import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Bell, Save, Filter, LogIn } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";

export default function Settings() {
  const navigate = useNavigate();
  const { user, updateSettings, logout, setShowEmailCapture } = useUser();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    auto_email_enabled: user?.auto_email_enabled ?? true,
    auto_email_threshold: user?.auto_email_threshold ?? 5,
    min_match_percentage: user?.min_match_percentage ?? 0,
    email_filter_mode: user?.email_filter_mode ?? "all",
  });

  // Update settings when user changes
  useEffect(() => {
    if (user) {
      setSettings({
        auto_email_enabled: user.auto_email_enabled ?? true,
        auto_email_threshold: user.auto_email_threshold ?? 5,
        min_match_percentage: user.min_match_percentage ?? 0,
        email_filter_mode: user.email_filter_mode ?? "all",
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  // If no user, show login prompt
  if (!user) {
    return (
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              Configuración
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestiona tu cuenta y preferencias
            </p>
          </div>
        </div>

        {/* Login Required */}
        <Card className="text-center py-12">
          <CardContent>
            <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">
              Inicia sesión para ver tu configuración
            </h3>
            <p className="text-muted-foreground mb-6">
              Necesitas ingresar tu correo para acceder a la configuración de tu cuenta.
            </p>
            <Button 
              onClick={() => setShowEmailCapture(true)}
              className="rounded-full"
            >
              Ingresar correo
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Configuración
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona tu cuenta y preferencias
          </p>
        </div>
      </div>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Cuenta
          </CardTitle>
          <CardDescription>Tu información de cuenta</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{user?.email}</p>
              <p className="text-sm text-muted-foreground">
                Miembro desde {new Date(user?.created_at || Date.now()).toLocaleDateString('es-ES')}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={logout}
              className="text-destructive hover:text-destructive"
              data-testid="sign-out-btn"
            >
              Cerrar sesión
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notificaciones por Email
          </CardTitle>
          <CardDescription>
            Configura cuándo recibir resúmenes de candidatos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Auto Email Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-email">Emails automáticos</Label>
              <p className="text-sm text-muted-foreground">
                Recibe un resumen cuando se suban suficientes candidatos
              </p>
            </div>
            <Switch
              id="auto-email"
              checked={settings.auto_email_enabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, auto_email_enabled: checked })
              }
              data-testid="auto-email-switch"
            />
          </div>

          {/* Threshold */}
          {settings.auto_email_enabled && (
            <div className="space-y-2">
              <Label>Enviar resumen cuando los candidatos lleguen a</Label>
              <Select
                value={settings.auto_email_threshold.toString()}
                onValueChange={(value) =>
                  setSettings({
                    ...settings,
                    auto_email_threshold: parseInt(value),
                  })
                }
              >
                <SelectTrigger className="w-48" data-testid="threshold-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 candidatos</SelectItem>
                  <SelectItem value="5">5 candidatos</SelectItem>
                  <SelectItem value="10">10 candidatos</SelectItem>
                  <SelectItem value="15">15 candidatos</SelectItem>
                  <SelectItem value="20">20 candidatos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Match Filter Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtro de Email
          </CardTitle>
          <CardDescription>
            Filtra qué candidatos incluir en los resúmenes por email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filter Mode */}
          <div className="space-y-3">
            <Label>Incluir en resúmenes por email</Label>
            <RadioGroup
              value={settings.email_filter_mode}
              onValueChange={(value) =>
                setSettings({ ...settings, email_filter_mode: value })
              }
              className="space-y-2"
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="all" id="filter-all" data-testid="filter-all" />
                <Label htmlFor="filter-all" className="font-normal cursor-pointer">
                  Todos los candidatos (vista completa)
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="above_threshold" id="filter-threshold" data-testid="filter-threshold" />
                <Label htmlFor="filter-threshold" className="font-normal cursor-pointer">
                  Solo candidatos sobre el % mínimo de match
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Minimum Match Percentage */}
          {settings.email_filter_mode === "above_threshold" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Porcentaje mínimo de match</Label>
                <span className="text-2xl font-heading font-bold text-primary">
                  {settings.min_match_percentage}%
                </span>
              </div>
              <Slider
                value={[settings.min_match_percentage]}
                onValueChange={(value) =>
                  setSettings({ ...settings, min_match_percentage: value[0] })
                }
                min={0}
                max={100}
                step={5}
                className="w-full"
                data-testid="min-match-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
              <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                Solo los candidatos con un score de match de <strong>{settings.min_match_percentage}%</strong> o más 
                serán incluidos en tus resúmenes por email.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gap-2 rounded-full"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </div>
    </div>
  );
}
